import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Plus,
    CalendarDays,
    Download,
    ExternalLink,
    Pencil,
    Save,
    X,
    Trash2,
    Loader2,
    FileText,
    Printer,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API, tokenStore } from "../lib/api";
import ProjectHeader from "../components/ProjectHeader";
import AddScheduleDialog from "../components/AddScheduleDialog";
import FlagButton from "../components/FlagButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";

/**
 * Iter-100 — Schedule simplified to a clean document store.
 *
 * - Owners upload PDFs / DOCXs / TXTs labelled by date + shoot day +
 *   title.
 * - List renders chronologically. Each card surfaces View / Open in
 *   new tab / Download / per-schedule notes / Delete.
 * - The previous auto-extracted scene rows + per-row notes flow has
 *   been retired (parser was unreliable). Per-schedule `day_note`
 *   replaces the row notes for the simple "what to remember for this
 *   day" use case.
 */
function formatShootDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString(undefined, {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

function NoteEditor({ value, placeholder, testid, onSave, canEdit, rows = 3 }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || "");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!editing) setDraft(value || "");
    }, [value, editing]);

    const start = () => {
        setDraft(value || "");
        setEditing(true);
    };
    const cancel = () => {
        setEditing(false);
        setDraft(value || "");
    };
    const save = async () => {
        setSaving(true);
        try {
            await onSave(draft);
            setEditing(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <div className="flex items-start gap-2">
                <p
                    data-testid={`${testid}-display`}
                    className="text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed flex-1 min-w-0"
                >
                    {value && value.trim() ? value : (
                        <span className="text-neutral-500 italic">{placeholder}</span>
                    )}
                </p>
                {canEdit ? (
                    <button
                        type="button"
                        data-testid={`${testid}-edit`}
                        onClick={start}
                        className="tap inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-neutral-400 hover:text-white hover:bg-white/5 shrink-0"
                    >
                        <Pencil size={11} />
                        {value && value.trim() ? "Edit" : "Add"}
                    </button>
                ) : null}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <Textarea
                data-testid={`${testid}-input`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={rows}
                maxLength={4000}
                placeholder={placeholder}
                autoFocus
                className="bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary resize-y text-sm"
            />
            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    data-testid={`${testid}-cancel`}
                    onClick={cancel}
                    disabled={saving}
                    className="tap inline-flex items-center justify-center h-7 w-7 rounded-md text-neutral-400 hover:text-white hover:bg-white/5"
                >
                    <X size={13} />
                </button>
                <Button
                    type="button"
                    data-testid={`${testid}-save`}
                    onClick={save}
                    disabled={saving || draft === (value || "")}
                    className="h-7 px-2 text-[11px] font-semibold"
                >
                    {saving ? (
                        <Loader2 size={11} className="animate-spin" />
                    ) : (
                        <>
                            <Save size={11} className="mr-1" />
                            Save
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
}

function ScheduleDayCard({ schedule, projectId, canNotes, canEdit, canDelete, onChanged, onDeleted, fileBase }) {
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState(schedule.title || "");
    const [savingTitle, setSavingTitle] = useState(false);
    const fileUrl = `${fileBase}/${schedule.id}/file?token=${encodeURIComponent(tokenStore.get() || "")}`;
    const downloadUrl = `${fileBase}/${schedule.id}/file?download=1&token=${encodeURIComponent(tokenStore.get() || "")}`;

    const saveDayNote = async (next) => {
        const { data } = await api.patch(
            `${apiBase}/${projectId}/schedules/${schedule.id}`,
            { day_note: next },
        );
        onChanged(data);
        toast.success("Note saved");
    };

    const startTitleEdit = () => {
        setTitleDraft(schedule.title || "");
        setEditingTitle(true);
    };
    const cancelTitleEdit = () => {
        if (savingTitle) return;
        setEditingTitle(false);
        setTitleDraft(schedule.title || "");
    };
    const saveTitle = async () => {
        const next = titleDraft.trim();
        if (next === (schedule.title || "")) {
            setEditingTitle(false);
            return;
        }
        setSavingTitle(true);
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/schedules/${schedule.id}`,
                { title: next },
            );
            onChanged(data);
            toast.success("Title updated");
            setEditingTitle(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingTitle(false);
        }
    };

    const remove = async () => {
        try {
            await api.delete(`${apiBase}/${projectId}/schedules/${schedule.id}`);
            toast.success("Schedule deleted");
            onDeleted(schedule.id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setConfirmingDelete(false);
        }
    };

    return (
        <article
            data-testid={`schedule-card-${schedule.id}`}
            className="rounded-xl border border-white/10 bg-card overflow-hidden"
        >
            <header className="p-4 border-b border-white/5">
                <div className="flex items-start gap-3">
                    <div className="shrink-0 mt-0.5">
                        <CalendarDays size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                            {schedule.shoot_day ? (
                                <span
                                    data-testid={`schedule-card-${schedule.id}-day`}
                                    className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full font-semibold bg-primary/15 text-primary border border-primary/30"
                                >
                                    Day {schedule.shoot_day}
                                </span>
                            ) : null}
                            {schedule.shoot_date ? (
                                <span
                                    data-testid={`schedule-card-${schedule.id}-date`}
                                    className="text-[11px] uppercase tracking-[0.2em] text-neutral-400"
                                >
                                    {formatShootDate(schedule.shoot_date)}
                                </span>
                            ) : null}
                        </div>
                        {editingTitle ? (
                            <div
                                data-testid={`schedule-card-${schedule.id}-title-editor`}
                                className="mt-1.5 flex items-center gap-1.5"
                            >
                                <Input
                                    autoFocus
                                    data-testid={`schedule-card-${schedule.id}-title-input`}
                                    value={titleDraft}
                                    onChange={(e) => setTitleDraft(e.target.value)}
                                    placeholder={schedule.filename}
                                    maxLength={120}
                                    className="h-9 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") saveTitle();
                                        if (e.key === "Escape") cancelTitleEdit();
                                    }}
                                />
                                <button
                                    type="button"
                                    data-testid={`schedule-card-${schedule.id}-title-save`}
                                    onClick={saveTitle}
                                    disabled={savingTitle}
                                    className="tap inline-flex items-center justify-center h-9 w-9 rounded-md bg-primary text-primary-foreground disabled:opacity-60"
                                    aria-label="Save title"
                                >
                                    {savingTitle ? (
                                        <Loader2 size={13} className="animate-spin" />
                                    ) : (
                                        <Save size={13} />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    data-testid={`schedule-card-${schedule.id}-title-cancel`}
                                    onClick={cancelTitleEdit}
                                    disabled={savingTitle}
                                    className="tap inline-flex items-center justify-center h-9 w-9 rounded-md text-neutral-400 hover:text-white hover:bg-white/5"
                                    aria-label="Cancel rename"
                                >
                                    <X size={13} />
                                </button>
                            </div>
                        ) : (
                            <div className="mt-1 flex items-center gap-1.5 group">
                                <h2
                                    data-testid={`schedule-card-${schedule.id}-title`}
                                    className="font-display text-lg font-semibold text-white truncate min-w-0 flex-1"
                                >
                                    {schedule.title || schedule.filename}
                                </h2>
                                {canEdit ? (
                                    <button
                                        type="button"
                                        data-testid={`schedule-card-${schedule.id}-title-edit`}
                                        onClick={startTitleEdit}
                                        title="Rename schedule"
                                        className="tap shrink-0 h-7 w-7 rounded-md text-neutral-500 hover:text-white hover:bg-white/5 inline-flex items-center justify-center"
                                    >
                                        <Pencil size={12} />
                                    </button>
                                ) : null}
                            </div>
                        )}
                        {schedule.filename ? (
                            <p
                                data-testid={`schedule-card-${schedule.id}-filename`}
                                className="text-[11px] text-neutral-500 mt-0.5 truncate flex items-center gap-1.5 mono"
                                title={schedule.filename}
                            >
                                <FileText size={10} />
                                {schedule.filename}
                            </p>
                        ) : null}
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                        <FlagButton
                            projectId={projectId}
                            entityType="schedule"
                            entityId={schedule.id}
                            entityLabel={schedule.title || schedule.filename || "schedule"}
                            testid={`schedule-card-${schedule.id}-flag`}
                        />
                        {canDelete ? (
                            <button
                                type="button"
                                data-testid={`schedule-card-${schedule.id}-delete`}
                                onClick={() => setConfirmingDelete(true)}
                                title="Delete schedule"
                                className="tap inline-flex items-center justify-center h-8 w-8 rounded-md text-neutral-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                                <Trash2 size={14} />
                            </button>
                        ) : null}
                    </div>
                </div>
            </header>

            {confirmingDelete ? (
                <div
                    data-testid={`schedule-card-${schedule.id}-confirm-delete`}
                    className="p-4 border-b border-white/5 bg-red-500/5 space-y-3"
                >
                    <p className="text-sm text-red-200">
                        Delete this schedule? Notes attached to it will also be removed.
                    </p>
                    <div className="flex gap-2 justify-end">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setConfirmingDelete(false)}
                            data-testid={`schedule-card-${schedule.id}-confirm-cancel`}
                            className="h-9"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={remove}
                            data-testid={`schedule-card-${schedule.id}-confirm-yes`}
                            className="h-9 bg-red-500 hover:bg-red-400 text-white"
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            ) : null}

            <div className="p-4 border-b border-white/5 grid grid-cols-2 gap-2">
                {/* Iter-157G P1 — schedule documents now open inside
                    the OSF viewer chrome (same shell as change
                    documents) so PDF / image schedules render
                    inline and DOCX schedules surface a calm
                    "Preview not available — download to open"
                    card. No more `target=_blank` to a raw file
                    URL. */}
                <Link
                    to={`${routeBase}/${projectId}/schedules/${schedule.id}/view`}
                    data-testid={`schedule-card-${schedule.id}-view`}
                    className="tap inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 h-10 text-[12px] font-semibold"
                >
                    <FileText size={13} />
                    View document
                </Link>
                <a
                    href={downloadUrl}
                    data-testid={`schedule-card-${schedule.id}-download`}
                    className="tap inline-flex items-center justify-center gap-1.5 rounded-md bg-secondary border border-white/10 text-neutral-200 hover:bg-white/10 px-3 h-10 text-[12px] font-semibold"
                >
                    <Download size={13} />
                    Download
                </a>
                {/* Iter-131a — browser-native print. Opens the file
                    in a hidden iframe + `iframe.contentWindow.print()`
                    so the user stays inside OSF instead of being
                    bounced into a tab. PDFs/DOCX both work because
                    we're delegating to the OS print pipeline. */}
                <button
                    type="button"
                    data-testid={`schedule-card-${schedule.id}-print`}
                    onClick={() => {
                        const ifr = document.createElement("iframe");
                        ifr.setAttribute("aria-hidden", "true");
                        ifr.setAttribute("title", "Print schedule");
                        ifr.style.position = "fixed";
                        ifr.style.right = "0";
                        ifr.style.bottom = "0";
                        ifr.style.width = "0";
                        ifr.style.height = "0";
                        ifr.style.border = "0";
                        ifr.style.opacity = "0";
                        ifr.src = fileUrl;
                        let printed = false;
                        ifr.addEventListener("load", () => {
                            if (printed) return;
                            printed = true;
                            try {
                                ifr.contentWindow?.focus();
                                ifr.contentWindow?.print();
                            } catch {
                                window.print();
                            }
                            setTimeout(() => {
                                try {
                                    document.body.removeChild(ifr);
                                } catch {
                                    /* ignore */
                                }
                            }, 1500);
                        });
                        document.body.appendChild(ifr);
                    }}
                    title="Print this schedule"
                    className="tap inline-flex items-center justify-center gap-1.5 rounded-md bg-secondary border border-white/10 text-neutral-200 hover:bg-white/10 px-3 h-10 text-[12px] font-semibold"
                >
                    <Printer size={13} />
                    Print
                </button>
            </div>

            <div className="p-4">
                <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                        Notes
                    </div>
                    {schedule.day_note ? (
                        <FlagButton
                            projectId={projectId}
                            entityType="schedule_note"
                            entityId={schedule.id}
                            entityLabel="schedule note"
                            size="xs"
                            testid={`schedule-card-${schedule.id}-note-flag`}
                        />
                    ) : null}
                </div>
                <NoteEditor
                    value={schedule.day_note}
                    placeholder="Notes for this schedule (call times, hair / makeup reminders, weather, etc.)"
                    testid={`schedule-card-${schedule.id}-day-note`}
                    canEdit={canNotes}
                    onSave={saveDayNote}
                    rows={3}
                />
            </div>
        </article>
    );
}

export default function SchedulePage() {
    const { projectId } = useParams();
    const apiBase = useApiBase();
    const [project, setProject] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [addOpen, setAddOpen] = useState(false);

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    const isArchived = Boolean(project?.archived);
    // Owners + admins manage uploads/deletes; anyone with cast/scene
    // edit perms can update notes.
    const canManage = (isOwner || isAdmin) && !isArchived;
    const canNotes =
        !isArchived &&
        (canManage ||
            Boolean(permissions?.scenes_edit) ||
            Boolean(permissions?.cast_edit));

    const fileBase = useMemo(
        () => `${API}${apiBase}/${projectId}/schedules`,
        [apiBase, projectId],
    );

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get(`${apiBase}/${projectId}/schedules`);
            setSchedules(data);
        } catch (e) {
            setError(formatApiError(e));
        }
    }, [apiBase, projectId]);

    useEffect(() => {
        const ctrl = new AbortController();
        (async () => {
            try {
                const [{ data: p }, { data: pm }, { data: sched }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`, { signal: ctrl.signal }),
                    api
                        .get(`${apiBase}/${projectId}/permissions/me`, { signal: ctrl.signal })
                        .catch(() => ({ data: null })),
                    api.get(`${apiBase}/${projectId}/schedules`, { signal: ctrl.signal }),
                ]);
                setProject(
                    // Iter-158.13.2.20f.3 #3 — unwrap Route 2 Hub GET.
                    (p && typeof p === "object" && p.project && p.project.id)
                        ? p.project : p,
                );
                setPermissions(pm?.permissions || {});
                setSchedules(sched);
            } catch (e) {
                if (ctrl.signal.aborted) return;
                setError(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
        return () => ctrl.abort();
    }, [apiBase, projectId]);

    const onCreated = (doc) => {
        // Iter-102 PART 5 — prepend newest so it's always visible
        // without scrolling. Backend also sorts created_at DESC so a
        // full reload would place it here anyway; this just saves a
        // round-trip.
        setSchedules((prev) => {
            const existingIdx = prev.findIndex((s) => s.id === doc.id);
            return existingIdx >= 0
                ? prev.map((s) => (s.id === doc.id ? doc : s))
                : [doc, ...prev];
        });
    };

    const onChanged = (doc) => {
        setSchedules((prev) => prev.map((s) => (s.id === doc.id ? doc : s)));
    };

    const onDeleted = (id) => {
        setSchedules((prev) => prev.filter((s) => s.id !== id));
    };

    return (
        <div data-testid="schedule-page" className="min-h-screen bg-background pb-48">
            <ProjectHeader projectName={project?.name} subtitle="Schedule" />

            <main className="max-w-2xl mx-auto px-5 pt-5">
                {/* Iter-134 — Removed local "Back to project" row to avoid
                    duplicate back affordances under ProjectHeader. */}
                <header className="mb-5 flex items-end justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div
                            data-testid="schedule-identity-icon"
                            className="h-10 w-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 flex items-center justify-center shrink-0"
                        >
                            <CalendarDays size={18} />
                        </div>
                        <div className="min-w-0">
                            <h1 className="font-display text-3xl font-bold text-white">
                                Schedule
                            </h1>
                            <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed">
                                Issued schedules and one-liners by date.
                                Upload the document, label it, and add notes.
                            </p>
                            {/* Iter-157H P1(6) — Schedule vs Notes
                                capability decision. Schedule is
                                intentionally simpler than Notes: a
                                single uploaded document per row, no
                                text-only entries, no external links,
                                no per-row visibility scoping.
                                Schedules are always visible to the
                                whole project team — for restricted
                                documents (call sheets with personal
                                data, NDA paperwork, etc.) use the
                                Documents section in Notes which
                                carries the full visibility picker.
                                This note documents the deliberate
                                choice so the inconsistency reads as
                                design, not oversight. */}
                            <p
                                data-testid="schedule-scope-note"
                                className="text-[11px] text-neutral-500 mt-2 italic leading-relaxed"
                            >
                                Schedules are visible to the whole project team.
                                For restricted documents, use Documents in Notes.
                            </p>
                        </div>
                    </div>
                    {canManage ? (
                        <Button
                            type="button"
                            data-testid="schedule-add-btn"
                            onClick={() => setAddOpen(true)}
                            className="shrink-0 min-tap"
                        >
                            <Plus size={16} className="mr-1" />
                            Add
                        </Button>
                    ) : null}
                </header>

                {error ? (
                    <p data-testid="schedule-error" className="text-sm text-red-400 mb-3">
                        {error}
                    </p>
                ) : null}

                {loading ? (
                    <p data-testid="schedule-loading" className="text-sm text-neutral-500">
                        Loading…
                    </p>
                ) : schedules.length === 0 ? (
                    <div
                        data-testid="schedule-empty"
                        className="rounded-xl border border-dashed border-white/10 bg-card/40 p-6 text-center"
                    >
                        <CalendarDays size={28} className="mx-auto text-neutral-600" />
                        <p className="text-sm text-neutral-400 mt-2">
                            No schedules yet.
                        </p>
                        {canManage ? (
                            <p className="text-xs text-neutral-500 mt-1">
                                Add a schedule or one-liner to start the archive.
                            </p>
                        ) : (
                            <p className="text-xs text-neutral-500 mt-1">
                                {/* Iter-158.13.2.20f.3 #3 — authority-aware
                                    empty-state copy. Legacy language said
                                    "the project owner"; Route 2 (and the
                                    modern legacy vocabulary) is the
                                    Production Owner. */}
                                Ask the Production Owner or an Administrator to upload a schedule.
                            </p>
                        )}
                    </div>
                ) : (
                    <ul data-testid="schedule-list" className="space-y-3">
                        {schedules.map((s) => (
                            <li key={s.id}>
                                <ScheduleDayCard
                                    schedule={s}
                                    projectId={projectId}
                                    canNotes={canNotes}
                                    canEdit={canManage}
                                    canDelete={canManage}
                                    onChanged={onChanged}
                                    onDeleted={onDeleted}
                                    fileBase={fileBase}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </main>

            <AddScheduleDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                projectId={projectId}
                onCreated={onCreated}
            />
        </div>
    );
}
